import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plane, BedDouble, Palmtree, Trash2, ShoppingCart, ArrowRight, Tag } from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/mock";
import { FlightCrossSell } from "@/components/CrossSell";

const kindIcon = { flight: Plane, hotel: BedDouble, holiday: Palmtree };

const CartPage = () => {
  const { items, remove, subtotal, discount, total, combo } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="flex items-center gap-2 font-[Poppins] text-2xl font-extrabold text-[#1B1F5B]">
          <ShoppingCart className="h-6 w-6" /> Your Trip Cart
        </h1>

        {items.length === 0 ? (
          <div data-testid="cart-empty" className="mt-8 rounded-3xl border border-dashed border-slate-200 bg-white py-16 text-center">
            <ShoppingCart className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Your trip cart is empty.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Link to="/" className="rounded-full bg-[#1B1F5B] px-5 py-2.5 text-xs font-extrabold text-white">Search Flights</Link>
              <Link to="/hotel-page" className="rounded-full border border-slate-200 px-5 py-2.5 text-xs font-extrabold text-slate-600">Browse Hotels</Link>
              <Link to="/package" className="rounded-full border border-slate-200 px-5 py-2.5 text-xs font-extrabold text-slate-600">View Holidays</Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr,340px]">
            <div className="space-y-3" data-testid="cart-items">
              {items.map((it) => {
                const Icon = kindIcon[it.kind] || Plane;
                return (
                  <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#0E7C6B]/10 text-[#0E7C6B]"><Icon className="h-5 w-5" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-bold text-slate-800">{it.title}</p>
                      <p className="truncate text-xs text-slate-400">{it.route}{it.date ? ` · ${it.date}` : ""}{it.pax ? ` · ${it.pax} pax` : ""}</p>
                      <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{it.kind}</span>
                    </div>
                    <p className="font-extrabold text-[#1B1F5B]">{formatINR(it.price)}</p>
                    <button data-testid={`cart-remove-${it.id}`} onClick={() => remove(it.id)} className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>

            <div className="h-fit rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="cart-summary">
              <h3 className="text-base font-extrabold text-slate-800">Trip Summary</h3>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                {combo && (
                  <div className="flex justify-between text-emerald-600" data-testid="cart-combo-discount">
                    <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Flight + Hotel combo (10%)</span>
                    <span>-{formatINR(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-dashed pt-2 text-base font-extrabold text-[#1B1F5B]"><span>Total</span><span data-testid="cart-total">{formatINR(total)}</span></div>
              </div>
              {combo && <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700">🎉 You saved {formatINR(discount)} by bundling flight + hotel!</p>}
              <button data-testid="cart-checkout-btn" onClick={() => navigate("/cart-checkout")}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5B32B] py-3 text-sm font-extrabold text-[#1B1F5B] hover:bg-[#e0a41f]">
                Proceed to Checkout <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {items.some((i) => i.kind === "hotel") && !items.some((i) => i.kind === "flight") && (
          <div className="mt-8"><FlightCrossSell city={items.find((i) => i.kind === "hotel")?.route?.split(",").pop()?.trim() || items.find((i) => i.kind === "hotel")?.title} /></div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CartPage;
